<template>
  <div class="student-wrong-question-book">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>
        <i class="el-icon-warning-outline"></i>
        我的错题集
      </h2>
      <p class="page-description">显示我在本课程中的错题，按错误次数排序</p>
    </div>

    <!-- 筛选条件 -->
    <div class="filter-section">
      <el-card class="filter-card">
        <div slot="header" class="filter-header">
          <span><i class="el-icon-filter"></i> 筛选条件</span>
          <el-button type="text" @click="resetFilters" size="small">重置</el-button>
        </div>
        <el-form :model="filters" :inline="true" class="filter-form">
          <el-form-item label="作业">
            <el-select v-model="filters.homeworkId" placeholder="选择作业" clearable @change="applyFilters">
              <el-option
                v-for="homework in filterOptions.homeworks"
                :key="homework.id"
                :label="homework.name"
                :value="homework.id">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="知识点">
            <el-select v-model="filters.knowledge" placeholder="选择知识点" clearable @change="applyFilters">
              <el-option
                v-for="knowledge in filterOptions.knowledges"
                :key="knowledge"
                :label="knowledge"
                :value="knowledge">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="applyFilters" :loading="loading">
              <i class="el-icon-search"></i> 筛选
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container" v-loading="loading" element-loading-text="正在加载错题数据..." element-loading-spinner="el-icon-loading" element-loading-background="rgba(255, 255, 255, 0.8)" style="height: 200px;">
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <el-alert
        :title="error"
        type="error"
        show-icon
        :closable="false">
      </el-alert>
      <div class="error-actions">
        <el-button type="primary" @click="loadWrongQuestions">重新加载</el-button>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else-if="!wrongQuestions || wrongQuestions.length === 0" class="empty-container">
      <div class="empty-content">
        <i class="el-icon-document"></i>
        <h3>暂无错题数据</h3>
        <p>你在本课程中还没有错题记录</p>
      </div>
    </div>

    <!-- 错题列表 -->
    <div v-else class="wrong-questions-content">
      <!-- 统计信息 -->
      <div class="statistics-card">
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="stat-item">
              <div class="stat-value">{{ statistics.totalQuestions }}</div>
              <div class="stat-label">错题总数</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="stat-item">
              <div class="stat-value">{{ statistics.totalWrongRecords }}</div>
              <div class="stat-label">错题记录</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="stat-item">
              <div class="stat-value">{{ averageWrongCount }}</div>
              <div class="stat-label">平均错误次数</div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 操作栏：选择并导出 -->
      <div class="actions-bar">
        <div class="left-actions">
          <el-checkbox v-model="selectAll" @change="handleSelectAllChange">全选</el-checkbox>
          <span class="selected-count" v-if="selectedCount > 0">已选择 {{ selectedCount }} 题</span>
        </div>
        <div class="right-actions">
          <el-button type="primary" :disabled="selectedCount === 0" @click="exportSelectedToWord" :loading="exporting">
            <i class="el-icon-download"></i> 导出选中为Word
          </el-button>
        </div>
      </div>

      <!-- 错题列表 -->
      <div class="wrong-questions-list">
        <div v-for="(question, index) in wrongQuestions" :key="question.questionId" class="question-card">
          <div class="question-header">
            <div class="question-info">
              <el-checkbox v-model="question.selected" @change="syncSelectState" class="select-checkbox"></el-checkbox>
              <span class="question-number">第{{ index + 1 }}题</span>
              <span class="question-chapter" v-if="question.chapter">{{ question.chapter }}</span>
              <span class="question-type">{{ getQuestionTypeText(question.questionType) }}</span>
              <span class="question-difficulty">{{ getDifficultyText(question.questionDifficulty) }}</span>
            </div>
            <div class="question-stats">
              <el-tag :type="getWrongCountType(question.wrongCount)" size="medium">
                错误 {{ question.wrongCount }} 次
              </el-tag>
            </div>
          </div>

          <div class="question-content">
            <div class="question-text" v-html="formatQuestionContent(question.questionContent)"></div>
          </div>

          <!-- 展开查看错题记录与详细信息 -->

          <!-- 错题记录详情 -->
          <div class="wrong-records">
            <el-button type="text" @click="toggleQuestionDetail(question)" class="detail-toggle">
              {{ question.expanded ? '收起详情' : '查看错题记录' }}
              <i :class="question.expanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
            </el-button>

            <div v-if="question.expanded" class="wrong-records-detail">
              <!-- 题目详细信息（选项/答案/解析/知识点/图片） -->
              <div v-if="question.questionOptions || question.questionStandardAnswer || question.questionAnalysis || question.relatedKnowledge || question.questionImageUrl" class="question-detail">
                <div v-if="question.questionImageUrl" class="question-image">
                  <img :src="question.questionImageUrl" alt="题目图片" @error="handleImageError">
                </div>
                <div v-if="question.questionOptions" class="question-options">
                  <div class="options-title">选项：</div>
                  <div class="options-content" v-html="formatOptions(question.questionOptions)"></div>
                </div>
                <div class="question-answer" v-if="question.questionStandardAnswer || question.questionAnalysis || question.relatedKnowledge">
                  <div v-if="question.questionStandardAnswer" class="answer-section">
                    <div class="answer-title">标准答案：</div>
                    <div class="answer-content" v-html="formatAnswer(question.questionStandardAnswer)"></div>
                  </div>
                  <div v-if="question.questionAnalysis" class="analysis-section">
                    <div class="analysis-title">题目解析：</div>
                    <div class="analysis-content" v-html="formatAnalysis(question.questionAnalysis)"></div>
                  </div>
                  <div v-if="question.relatedKnowledge" class="knowledge-section">
                    <div class="knowledge-title">相关知识点：</div>
                    <div class="knowledge-content">{{ question.relatedKnowledge }}</div>
                  </div>
                </div>
              </div>

              <div v-for="(record, recordIndex) in question.wrongRecords" :key="recordIndex" class="wrong-record-item">
                <div class="record-header">
                  <span class="record-time">{{ formatTime(record.wrongTime) }}</span>
                  <span class="record-homework">{{ record.homeworkName }}</span>
                </div>
                <div class="record-content">
                  <div v-if="record.studentAnswerText" class="student-answer">
                    <div class="answer-label">我的答案：</div>
                    <div class="answer-text" v-html="formatAnswer(record.studentAnswerText)"></div>
                  </div>
                  <div v-if="record.studentAnswerImageUrl" class="student-answer-image">
                    <div class="answer-label">我的答案图片：</div>
                    <img :src="record.studentAnswerImageUrl" alt="我的答案" @error="handleImageError">
                  </div>
                  <div v-if="record.teacherComment" class="teacher-comment">
                    <div class="comment-label">老师评语：</div>
                    <div class="comment-text">{{ record.teacherComment }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getStudentCourseWrongQuestions, getStudentCourseWrongQuestionsFilterOptions } from '@/api/homeworkSubmit'
import { Document, Packer, Paragraph, HeadingLevel, TextRun, ImageRun } from 'docx'

export default {
  name: 'StudentWrongQuestionBook',
  props: {
    courseId: {
      type: [String, Number],
      required: true
    },
    studentId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      wrongQuestions: [],
      statistics: {
        totalQuestions: 0,
        totalWrongRecords: 0
      },
      filters: {
        homeworkId: null,
        knowledge: null
      },
      filterOptions: {
        homeworks: [],
        knowledges: []
      },
      // 选择与导出
      selectAll: false,
      exporting: false
    }
  },
  computed: {
    averageWrongCount() {
      if (!this.wrongQuestions || this.wrongQuestions.length === 0) {
        return 0
      }
      const totalCount = this.wrongQuestions.reduce((sum, question) => {
        return sum + question.wrongCount
      }, 0)
      return (totalCount / this.wrongQuestions.length).toFixed(1)
    },
    selectedQuestions() {
      return (this.wrongQuestions || []).filter(q => q.selected)
    },
    selectedCount() {
      return this.selectedQuestions.length
    }
  },
  mounted() {
    this.loadFilterOptions()
    this.loadWrongQuestions()
  },
  methods: {
    async loadWrongQuestions() {
      if (!this.courseId || !this.studentId) {
        this.error = '课程ID或学生ID不能为空'
        return
      }

      this.loading = true
      this.error = null

      try {
        console.log('开始加载学生课程错题集，课程ID:', this.courseId, '学生ID:', this.studentId, '筛选条件:', this.filters)
        const response = await getStudentCourseWrongQuestions(this.courseId, this.studentId, this.filters)
        console.log('学生错题集API响应:', response)

        if (response.code === 1) {
          const list = response.data.uniqueQuestions || []
          // 初始化选择状态
          this.wrongQuestions = list.map(q => ({ ...q, selected: false, expanded: false }))
          this.statistics = response.data.statistics || {}
          console.log('学生错题集加载成功:', {
            questions: this.wrongQuestions.length,
            statistics: this.statistics
          })
        } else {
          this.error = response.msg || '获取错题集失败'
        }
      } catch (error) {
        console.error('加载学生错题集失败:', error)
        this.error = '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },

    // 全选/取消全选
    handleSelectAllChange(val) {
      (this.wrongQuestions || []).forEach(q => { q.selected = !!val })
    },
    // 同步全选的勾选状态
    syncSelectState() {
      const total = (this.wrongQuestions || []).length
      const selected = (this.wrongQuestions || []).filter(q => q.selected).length
      this.selectAll = total > 0 && selected === total
    },

    // 导出选中错题为Word（仅题干与选项，不含答案/解析）
    async exportSelectedToWord() {
      if (this.selectedCount === 0 || this.exporting) return
      try {
        this.exporting = true

        const docChildren = []

        // 文档标题
        docChildren.push(new Paragraph({
          text: '错题练习（导出）',
          heading: HeadingLevel.HEADING_1
        }))

        const dateStr = new Date().toLocaleString()
        docChildren.push(new Paragraph({
          children: [new TextRun({ text: `导出时间：${dateStr}`, size: 20 })]
        }))

        // 按题目类型分组
        const groupedQuestions = this.groupQuestionsByType(this.selectedQuestions)

        let globalQuestionIndex = 1

        // 按类型分组添加题目
        for (const [typeName, questions] of Object.entries(groupedQuestions)) {
          if (questions.length === 0) continue

          // 添加题型标题
          docChildren.push(new Paragraph({
            text: `${typeName}（共${questions.length}题）`,
            heading: HeadingLevel.HEADING_2
          }))

          // 添加该类型下的所有题目
          for (let i = 0; i < questions.length; i++) {
            const q = questions[i]
            const indexTitle = `第 ${globalQuestionIndex} 题（${this.getDifficultyText(q.questionDifficulty)}）`
            docChildren.push(new Paragraph({
              text: indexTitle,
              heading: HeadingLevel.HEADING_3
            }))

            // 题干（转为纯文本）
            const contentText = this.stripHtml(q.questionContent || '')
            docChildren.push(new Paragraph({ children: [new TextRun({ text: contentText })] }))

            // 图片（如果有）
            if (q.questionImageUrl) {
              try {
                const imgBuf = await this.fetchImageArrayBuffer(q.questionImageUrl)
                if (imgBuf) {
                  docChildren.push(new Paragraph({
                    children: [
                      new ImageRun({ data: imgBuf, transformation: { width: 480, height: 320 } })
                    ]
                  }))
                }
              } catch (e) {
                // 忽略图片失败
              }
            }

            // 选项（如有）
            const optionsText = this.formatOptionsForText(q.questionOptions)
            if (optionsText) {
              const lines = optionsText.split('\n')
              lines.forEach(line => {
                docChildren.push(new Paragraph({ children: [new TextRun({ text: line })] }))
              })
            }

            // 留空作答区域
            docChildren.push(new Paragraph({ text: '' }))
            docChildren.push(new Paragraph({ children: [new TextRun({ text: '作答：______________________________' })] }))
            docChildren.push(new Paragraph({ text: '' }))

            globalQuestionIndex++
          }
        }

        const doc = new Document({ sections: [{ properties: {}, children: docChildren }] })
        const blob = await Packer.toBlob(doc)

        const fileName = `错题练习_${new Date().getTime()}.docx`
        this.triggerDownload(blob, fileName)
        this.$message.success('Word 导出成功')
      } catch (error) {
        console.error('导出Word失败:', error)
        this.$message.error('导出失败，请重试')
      } finally {
        this.exporting = false
      }
    },

    // 按题目类型分组
    groupQuestionsByType(questions) {
      // 定义题目类型优先级和名称
      const typeConfig = {
        1: { name: '一、单选题', order: 1 },
        2: { name: '二、多选题', order: 2 },
        3: { name: '三、判断题', order: 3 },
        4: { name: '四、填空题', order: 4 },
        5: { name: '五、简答题', order: 5 },
        6: { name: '六、计算题', order: 6 }
      }

      // 按类型分组
      const groups = {}
      questions.forEach(q => {
        const typeInfo = typeConfig[q.questionType] || { name: '其他题型', order: 999 }
        if (!groups[typeInfo.name]) {
          groups[typeInfo.name] = []
        }
        groups[typeInfo.name].push(q)
      })

      // 对每个组内的题目按难度排序
      Object.keys(groups).forEach(typeName => {
        groups[typeName].sort((a, b) => {
          const difficultyOrder = { 1: 1, 2: 2, 3: 3 }
          const diffA = difficultyOrder[a.questionDifficulty] || 999
          const diffB = difficultyOrder[b.questionDifficulty] || 999
          return diffA - diffB
        })
      })

      // 按类型顺序排序分组
      const sortedGroups = {}
      Object.keys(typeConfig)
        .sort((a, b) => typeConfig[a].order - typeConfig[b].order)
        .forEach(typeId => {
          const typeInfo = typeConfig[typeId]
          if (groups[typeInfo.name]) {
            sortedGroups[typeInfo.name] = groups[typeInfo.name]
          }
        })

      // 添加其他未定义的类型
      Object.keys(groups).forEach(typeName => {
        if (!sortedGroups[typeName]) {
          sortedGroups[typeName] = groups[typeName]
        }
      })

      return sortedGroups
    },

    stripHtml(html) {
      if (!html) return ''
      const tmp = document.createElement('div')
      tmp.innerHTML = html
      const text = (tmp.textContent || tmp.innerText || '').replace(/\u00A0/g, ' ').trim()
      return text
    },

    formatOptionsForText(options) {
      if (!options) return ''
      try {
        const optionObj = JSON.parse(options)
        const lines = []
        for (const [key, value] of Object.entries(optionObj)) {
          const textValue = this.stripHtml(String(value))
          lines.push(`${key}. ${textValue}`)
        }
        return lines.join('\n')
      } catch (e) {
        const plain = this.stripHtml(String(options))
        return plain
      }
    },

    async fetchImageArrayBuffer(url) {
      const res = await fetch(url)
      const buf = await res.arrayBuffer()
      return buf
    },

    triggerDownload(blob, fileName) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    // 加载筛选选项
    async loadFilterOptions() {
      if (!this.courseId || !this.studentId) {
        return
      }

      try {
        console.log('开始加载学生筛选选项，课程ID:', this.courseId, '学生ID:', this.studentId)
        const response = await getStudentCourseWrongQuestionsFilterOptions(this.courseId, this.studentId)
        console.log('学生筛选选项API响应:', response)

        if (response.code === 1) {
          this.filterOptions = response.data || {
            homeworks: [],
            knowledges: []
          }
          console.log('学生筛选选项加载成功:', this.filterOptions)
        } else {
          console.error('学生筛选选项加载失败:', response.msg)
        }
      } catch (error) {
        console.error('加载学生筛选选项失败:', error)
      }
    },

    // 应用筛选
    applyFilters() {
      console.log('应用筛选条件:', this.filters)
      this.loadWrongQuestions()
    },

    // 重置筛选条件
    resetFilters() {
      this.filters = {
        homeworkId: null,
        knowledge: null
      }
      console.log('重置筛选条件')
      this.loadWrongQuestions()
    },

    getQuestionTypeText(type) {
      const typeMap = {
        1: '单选题',
        2: '多选题',
        3: '判断题',
        4: '填空题',
        5: '简答题',
        6: '计算题'
      }
      return typeMap[type] || '未知类型'
    },

    getDifficultyText(difficulty) {
      const difficultyMap = {
        1: '简单',
        2: '中等',
        3: '困难'
      }
      return difficultyMap[difficulty] || '未知难度'
    },

    getWrongCountType(count) {
      if (count >= 3) return 'danger'
      if (count >= 2) return 'warning'
      return 'info'
    },

    formatQuestionContent(content) {
      if (!content) return ''
      return content.replace(/\n/g, '<br>')
    },

    formatOptions(options) {
      if (!options) return ''
      try {
        const optionObj = JSON.parse(options)
        let html = ''
        for (const [key, value] of Object.entries(optionObj)) {
          html += `<div class="option-item"><strong>${key}:</strong> ${value}</div>`
        }
        return html
      } catch (e) {
        return options.replace(/\n/g, '<br>')
      }
    },

    formatAnswer(answer) {
      if (!answer) return ''
      return answer.replace(/\n/g, '<br>')
    },

    formatAnalysis(analysis) {
      if (!analysis) return ''
      return analysis.replace(/\n/g, '<br>')
    },

    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleString()
    },

    handleImageError(event) {
      event.target.style.display = 'none'
    },

    toggleQuestionDetail(question) {
      this.$set(question, 'expanded', !question.expanded)
    }
  }
}
</script>

<style scoped>
.student-wrong-question-book {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 24px;
  font-weight: bold;
}

.page-header h2 i {
  margin-right: 8px;
  color: #E6A23C;
}

.page-description {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  color: #303133;
}

.filter-header i {
  margin-right: 5px;
  color: #409EFF;
}

.filter-form {
  margin-top: 10px;
}

.filter-form .el-form-item {
  margin-bottom: 10px;
}

.filter-form .el-select {
  width: 200px;
}

.loading-container,
.error-container,
.empty-container {
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.error-actions {
  margin-top: 20px;
}

.empty-content i {
  font-size: 64px;
  color: #C0C4CC;
  margin-bottom: 20px;
}

.empty-content h3 {
  margin: 0 0 10px 0;
  color: #606266;
}

.empty-content p {
  margin: 0;
  color: #909399;
}

.wrong-questions-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.statistics-card {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #eaeaea;
  border-bottom: 1px solid #eaeaea;
  background: #fff;
}

.selected-count {
  margin-left: 12px;
  color: #606266;
}

.select-checkbox {
  margin-right: 10px;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
}

.wrong-questions-list {
  padding: 12px;
}

.question-card {
  border: 1px solid #EBEEF5;
  border-radius: 8px;
  margin-bottom: 12px;
  background: white;
  transition: all 0.3s ease;
}

.question-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #F8F9FA;
  border-bottom: 1px solid #EBEEF5;
}

.question-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.question-number {
  font-weight: bold;
  color: #409EFF;
}

.question-chapter,
.question-type,
.question-difficulty {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  background: #E1F3D8;
  color: #67C23A;
}

.question-content {
  padding: 12px 14px;
}

.question-text {
  font-size: 16px;
  line-height: 1.6;
  color: #303133;
  margin-bottom: 15px;
}

.question-image {
  margin: 10px 0;
}

.question-image img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.question-options {
  margin: 10px 0;
  padding: 12px;
  background: #F8F9FA;
  border-radius: 6px;
}

.options-title {
  font-weight: bold;
  margin-bottom: 10px;
  color: #606266;
}

.option-item {
  margin-bottom: 5px;
  line-height: 1.5;
}

.question-answer {
  padding: 12px 14px;
  background: #F0F9FF;
  border-top: 1px solid #EBEEF5;
}

.answer-section,
.analysis-section,
.knowledge-section {
  margin-bottom: 15px;
}

.answer-title,
.analysis-title,
.knowledge-title {
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}

.answer-content,
.analysis-content,
.knowledge-content {
  color: #606266;
  line-height: 1.6;
}

.wrong-records {
  padding: 12px 14px;
  border-top: 1px solid #EBEEF5;
}

.detail-toggle {
  width: 100%;
  text-align: center;
  color: #409EFF;
  font-weight: bold;
}

.wrong-records-detail {
  margin-top: 15px;
}

.wrong-record-item {
  border: 1px solid #EBEEF5;
  border-radius: 6px;
  margin-bottom: 15px;
  background: #FAFAFA;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #F5F7FA;
  border-bottom: 1px solid #EBEEF5;
}

.record-time {
  color: #909399;
  font-size: 12px;
}

.record-homework {
  color: #606266;
  font-weight: bold;
}

.record-content {
  padding: 15px;
}

.student-answer,
.student-answer-image,
.teacher-comment {
  margin-bottom: 15px;
}

.answer-label,
.comment-label {
  font-weight: bold;
  color: #E6A23C;
  margin-bottom: 5px;
}

.answer-text,
.comment-text {
  color: #606266;
  line-height: 1.6;
}

.student-answer-image img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
